import type { Grid } from "../game/board";
import type { Pos } from "../game/types";
import { idx } from "../game/board";
import { TIERS, tier } from "../game/tiers";
import { buildDefs } from "./defs";
import { tierLabel, type LabelMode } from "./notation";
import { tileArtSvg, tileGlowFilter } from "./tileArt";

const SVG_NS = "http://www.w3.org/2000/svg";
/** Logical units per cell in the SVG viewBox. */
export const UNIT = 100;

interface CellNode {
  group: SVGGElement;
  art: SVGGElement;
  label: SVGTextElement;
  tier: number; // currently rendered tier (0 = empty)
}

/**
 * Renders a Grid into an SVG element. One persistent node per cell; updates
 * toggle visibility/fill with CSS transitions so merges and settles animate.
 */
export class BoardRenderer {
  readonly svg: SVGSVGElement;
  private readonly cols: number;
  private readonly rows: number;
  private readonly nodes: CellNode[] = [];
  private labelMode: LabelMode = "number";

  constructor(grid: Grid) {
    this.cols = grid.cols;
    this.rows = grid.rows;
    this.svg = document.createElementNS(SVG_NS, "svg");
    this.svg.setAttribute("viewBox", `0 0 ${this.cols * UNIT} ${this.rows * UNIT}`);
    this.svg.setAttribute("class", "board-svg");
    this.svg.setAttribute("role", "grid");
    this.svg.innerHTML = buildDefs();
    this.buildCells(grid);
  }

  private buildCells(grid: Grid): void {
    // Board panel backdrop behind the grid.
    const boardBg = document.createElementNS(SVG_NS, "rect");
    boardBg.setAttribute("x", "2");
    boardBg.setAttribute("y", "2");
    boardBg.setAttribute("width", String(this.cols * UNIT - 4));
    boardBg.setAttribute("height", String(this.rows * UNIT - 4));
    boardBg.setAttribute("rx", "22");
    boardBg.setAttribute("class", "board-bg");
    this.svg.appendChild(boardBg);

    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        const x = c * UNIT + UNIT / 2;
        const y = r * UNIT + UNIT / 2;
        const blocked = grid.blocked[idx(grid, r, c)];

        // Cell backdrop.
        const back = document.createElementNS(SVG_NS, "rect");
        back.setAttribute("x", String(c * UNIT + 4));
        back.setAttribute("y", String(r * UNIT + 4));
        back.setAttribute("width", String(UNIT - 8));
        back.setAttribute("height", String(UNIT - 8));
        back.setAttribute("rx", "14");
        back.setAttribute("class", blocked ? "cell-back cell-blocked" : "cell-back");
        this.svg.appendChild(back);

        const group = document.createElementNS(SVG_NS, "g");
        group.setAttribute("class", "tile");
        group.setAttribute("transform", `translate(${x} ${y})`);
        group.style.opacity = "0";

        // Celestial art layer (replaced per tier) + label on top.
        const art = document.createElementNS(SVG_NS, "g");
        art.setAttribute("class", "tile-art");
        group.appendChild(art);

        const label = document.createElementNS(SVG_NS, "text");
        label.setAttribute("text-anchor", "middle");
        label.setAttribute("dy", "0.35em");
        label.setAttribute("class", "tile-label");
        group.appendChild(label);

        this.svg.appendChild(group);
        this.nodes[idx(grid, r, c)] = { group, art, label, tier: 0 };
      }
    }
  }

  setLabelMode(mode: LabelMode): void {
    if (mode === this.labelMode) return;
    this.labelMode = mode;
    // Re-label every visible tile in place.
    for (const node of this.nodes) {
      if (node.tier > 0) {
        node.label.textContent = tierLabel(node.tier, this.labelMode);
        node.label.setAttribute("class", this.labelMode === "name" ? "tile-label tile-label-name" : "tile-label");
      }
    }
  }

  /** Sync the visuals to the current grid state. */
  render(grid: Grid): void {
    this.renderCells(grid.cells);
  }

  /** Sync the visuals to an arbitrary cell snapshot (used for frame playback). */
  renderCells(cells: readonly number[]): void {
    for (let i = 0; i < this.nodes.length; i++) {
      this.updateCell(this.nodes[i], cells[i]);
    }
  }

  /**
   * Pixel center of a cell in client coordinates (for FX overlays).
   *
   * The SVG viewBox is 5:8 — if the rendered box has a different aspect ratio
   * the content is centered with letterboxing (default preserveAspectRatio).
   * We compute the cell center in viewBox space and map it through the SVG's
   * actual on-screen transform so FX line up exactly with the rendered tiles.
   */
  cellClientPos(r: number, c: number): { x: number; y: number } {
    const ctm = this.svg.getScreenCTM();
    if (!ctm) {
      const rect = this.svg.getBoundingClientRect();
      return {
        x: rect.left + ((c + 0.5) / this.cols) * rect.width,
        y: rect.top + ((r + 0.5) / this.rows) * rect.height,
      };
    }
    const pt = this.svg.createSVGPoint();
    pt.x = c * UNIT + UNIT / 2;
    pt.y = r * UNIT + UNIT / 2;
    const screen = pt.matrixTransform(ctm);
    return { x: screen.x, y: screen.y };
  }

  /** Mark a cell as the current targeting selection. */
  setTargetHighlight(cells: Pos[]): void {
    this.svg.querySelectorAll(".target-marker").forEach((el) => el.remove());
    for (const p of cells) {
      const m = document.createElementNS(SVG_NS, "rect");
      m.setAttribute("x", String(p.c * UNIT + 3));
      m.setAttribute("y", String(p.r * UNIT + 3));
      m.setAttribute("width", String(UNIT - 6));
      m.setAttribute("height", String(UNIT - 6));
      m.setAttribute("rx", "14");
      m.setAttribute("class", "target-marker");
      this.svg.appendChild(m);
    }
  }

  /**
   * Convert client (pixel) coords into SVG user-space (viewBox) coords.
   * Uses the screen CTM so it stays correct under `preserveAspectRatio`
   * letterboxing — `getBoundingClientRect` would map the element box, not the
   * drawn board, which skews column hit-testing at the edges.
   */
  clientToSvg(clientX: number, clientY: number): { x: number; y: number } | null {
    const ctm = this.svg.getScreenCTM();
    if (!ctm) return null;
    const pt = this.svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const local = pt.matrixTransform(ctm.inverse());
    return { x: local.x, y: local.y };
  }

  /** Column under a client x, clamped to the board (for drop targeting). */
  colFromClient(clientX: number): number {
    const p = this.clientToSvg(clientX, 0);
    const c = p ? Math.floor(p.x / UNIT) : 0;
    return Math.min(this.cols - 1, Math.max(0, c));
  }

  /** Convert client coords to a grid cell, or null if outside the board. */
  cellFromClient(clientX: number, clientY: number): Pos | null {
    const p = this.clientToSvg(clientX, clientY);
    if (!p) return null;
    const c = Math.floor(p.x / UNIT);
    const r = Math.floor(p.y / UNIT);
    if (c < 0 || c >= this.cols || r < 0 || r >= this.rows) return null;
    return { r, c };
  }

  private updateCell(node: CellNode, value: number): void {
    if (value === node.tier) return;
    const wasEmpty = node.tier === 0;
    node.tier = value;

    if (value === 0) {
      node.group.style.opacity = "0";
      node.group.classList.remove("tile-pop");
      return;
    }

    const isSpec = value < 0;
    node.art.innerHTML = tileArtSvg(value);
    const filter = tileGlowFilter(value);
    if (filter) node.art.setAttribute("filter", filter);
    else node.art.removeAttribute("filter");
    if (isSpec) {
      // Bosses show their remaining HP; wild/bomb show no label.
      const hp = value <= -100 ? -100 - value : 0;
      node.label.textContent = hp > 0 ? String(hp) : "";
      node.label.setAttribute("class", hp > 0 ? "tile-label tile-label-boss" : "tile-label");
      node.group.setAttribute(
        "aria-label",
        hp > 0 ? `boss (${hp} hp)` : value === -1 ? "wild" : "bomb",
      );
    } else {
      const tr = tier(value);
      node.label.textContent = tierLabel(value, this.labelMode);
      node.label.setAttribute(
        "class",
        this.labelMode === "name" ? "tile-label tile-label-name" : "tile-label",
      );
      node.group.setAttribute("aria-label", `${tr.key} (${value})`);
    }
    node.group.style.opacity = "1";

    // Pop animation on appearance / promotion.
    node.group.classList.remove("tile-pop");
    void node.group.getBoundingClientRect(); // reflow to restart animation
    node.group.classList.add("tile-pop");
    if (wasEmpty) {
      node.group.classList.add("tile-pop-strong");
      setTimeout(() => node.group.classList.remove("tile-pop-strong"), 260);
    }
  }

  /** Highlight a column (drop preview). */
  setColumnHighlight(col: number | null): void {
    this.svg.querySelectorAll(".col-marker").forEach((el) => el.remove());
    if (col === null) return;
    const marker = document.createElementNS(SVG_NS, "rect");
    marker.setAttribute("x", String(col * UNIT + 2));
    marker.setAttribute("y", "0");
    marker.setAttribute("width", String(UNIT - 4));
    marker.setAttribute("height", String(this.rows * UNIT));
    marker.setAttribute("rx", "12");
    marker.setAttribute("class", "col-marker");
    // Sit above the board panel but below the cells/tiles.
    const boardBg = this.svg.querySelector(".board-bg");
    if (boardBg && boardBg.nextSibling) this.svg.insertBefore(marker, boardBg.nextSibling);
    else this.svg.appendChild(marker);
  }
}

/** Build a small standalone SVG snippet for a single tier (used in HUD/next). */
export function tierSwatch(level: number, size = 44): string {
  const filter = tileGlowFilter(level);
  return `<svg viewBox="0 0 100 100" width="${size}" height="${size}" class="swatch" aria-hidden="true">
    ${buildDefs()}
    <g transform="translate(50 50)" ${filter ? `filter="${filter}"` : ""}>${tileArtSvg(level)}</g>
  </svg>`;
}

export { TIERS };
